// ABOUTME: Pivot table component for the report hub, supporting large datasets (200+ cols, 400+ rows).
// ABOUTME: Uses react-virtualized Grid with ScrollSync for performant cell virtualization.
import SortIcon from '@mui/icons-material/Sort';
import { Box, alpha, useTheme } from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AutoSizer, Grid, ScrollSync, WindowScroller } from "react-virtualized";
import Subheading from "../../../theme/text/Subheading";
import Text from "../../../theme/text/Text";
import { tokens } from "../../../../theme";
import { RhPivotTableRow } from "./pivottable/RhPivotTableRow";
import { RhDeltaPill } from "./pivottable/RhDeltaPill";
import { RhTrendPills } from "./pivottable/RhTrendPills";
import { RhPivotPeriodMode } from "./pivottable/RhPivotPeriodMode";
import { RhPivotCompareMode } from "./pivottable/RhPivotCompareMode";
import { RhPivotRowDetail } from "./pivottable/RhPivotRowDetail";
import { getPivotView, pivotViewModes, pivotTrendVariants, isPivotRowDrillable, pivotRowCursor } from "../reportUtils";
import { RhHeatmapToggle } from "./pivottable/RhHeatmapToggle";
import ShGridError from '../../../theme/datagrid2/Status/ShGridError';
import ShTextSkeleton from '../../../theme/skeleton/ShTextSkeleton';
import SHModal from '../../../theme/Modal/SHModal';
import EMScanStatus from '../../ModalsPopover/explainers/bi/EMScanStatus';
import { getScanStatusMeta, scanStatusColor } from '../scanstatusPresentation';

const ROW_HEIGHT = 48;
const HEADER_HEIGHT = 72;
// A scanstatus column states its own rule under its name, so the header band needs the extra room.
const SCANSTATUS_HEADER_HEIGHT = 104;
const FOOTER_HEIGHT = 120;
const COLUMN_WIDTH = 200;
const SKELETON_DELAY_MS = 400;
const SKELETON_ROW_COUNT = 12;

// Deterministic widths for skeleton rows so they don't shift on re-render
const SKELETON_WIDTHS = [...Array(SKELETON_ROW_COUNT)].map((_, i) => ({
    left: `${60 + ((i * 17) % 40)}%`,
    mid: `${40 + ((i * 13) % 30)}%`,
    right: `${50 + ((i * 11) % 30)}%`,
}));

export const RhPivotTable = (props) => {
    const { t, ready, i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const pivotOuterRef = useRef(null);
    const centerGridRef = useRef(null);
    const scrollPosRef = useRef({ scrollLeft: 0, scrollTop: 0 });
    const dragState = useRef({ isDragging: false, startX: 0, startY: 0, scrollLeft: 0, scrollTop: 0, direction: null, wasDragging: false });

    // Sort state: colIndex can be a number (date column) or 'total', direction is 'asc' | 'desc' | null
    const [sortState, setSortState] = useState({ colIndex: null, direction: null });

    // Selected row for drill-in modal (index into sortedRows)
    const [selectedRowIndex, setSelectedRowIndex] = useState(null);

    // Heatmap mode: 'off' | 'row' | 'global' — controlled by parent (toggle in page header)
    const heatmapMode = props.heatmapMode || 'off';

    // A scanstatus pivot names its own columns, so they can explain themselves. Detected from the data
    // rather than from the report config: the backend hands the bucket key back as the column name, so a
    // column that is a scanstatus bucket says so itself and every other dimension is untouched.
    const scanStatusColumns = useMemo(
        () => (props.data?.columns || []).map(getScanStatusMeta),
        [props.data?.columns]
    );
    const hasScanStatusColumns = scanStatusColumns.some(Boolean);
    const headerHeight = hasScanStatusColumns ? SCANSTATUS_HEADER_HEIGHT : HEADER_HEIGHT;

    // Pivot view settings (trend pills etc.).
    const view = getPivotView(props.currentReport);
    // The left (row label) column grows by one 130px slot per enabled trend variant.
    const LEFT_WIDTH = 400 + view.trends.length * 130;

    // Compute sorted rows based on current sort state
    const sortedRows = useMemo(() => {
        if (!props.data?.rows) return [];
        if (sortState.colIndex === null || sortState.direction === null) return props.data.rows;

        const sorted = [...props.data.rows];
        const dir = sortState.direction === 'asc' ? 1 : -1;

        sorted.sort((a, b) => {
            const aVal = sortState.colIndex === 'total' ? a.total : a.values[sortState.colIndex];
            const bVal = sortState.colIndex === 'total' ? b.total : b.values[sortState.colIndex];

            // Numeric comparison when possible
            const aNum = Number(aVal);
            const bNum = Number(bVal);
            if (!isNaN(aNum) && !isNaN(bNum)) {
                return (aNum - bNum) * dir;
            }
            return String(aVal ?? '').localeCompare(String(bVal ?? '')) * dir;
        });

        return sorted;
    }, [props.data?.rows, sortState.colIndex, sortState.direction]);

    // Heatmap cell backgrounds — precomputed 2D array so cell render is a pure O(1) lookup.
    // null when toggled off; otherwise heatmapColors[rowIndex][columnIndex] = bgColor | undefined
    const heatmapColors = useMemo(() => {
        if (heatmapMode === 'off') return null;
        // Build readable tints from the brand colour with custom alpha steps,
        // so text contrast stays high (palette[200]+ is too dark for default text).
        const baseHex = theme.palette.mode === 'dark' ? colors.blue[400] : colors.primary[400];
        const h = baseHex.replace('#', '');
        const r = parseInt(h.slice(0, 2), 16);
        const g = parseInt(h.slice(2, 4), 16);
        const b = parseInt(h.slice(4, 6), 16);
        const tint = (a) => `rgba(${r},${g},${b},${a})`;

        const intensityToTint = (intensity) => {
            if (intensity >= 0.75) return tint(0.28);
            if (intensity >= 0.5) return tint(0.18);
            if (intensity >= 0.25) return tint(0.08);
            return undefined;
        };

        if (heatmapMode === 'global') {
            // One min/max across all cells — absolute volume drives intensity.
            let min = Infinity, max = -Infinity;
            for (const row of sortedRows) {
                for (const v of row.values || []) {
                    const n = Number(v);
                    if (isFinite(n)) {
                        if (n < min) min = n;
                        if (n > max) max = n;
                    }
                }
            }
            if (!isFinite(min) || max === min) {
                return sortedRows.map(row => (row.values || []).map(() => undefined));
            }
            const range = max - min;
            return sortedRows.map(row => (row.values || []).map(v => {
                const n = Number(v);
                if (!isFinite(n)) return undefined;
                return intensityToTint((n - min) / range);
            }));
        }

        // 'row' — each row normalised to its own range (intra-row pattern).
        return sortedRows.map(row => {
            const values = row.values || [];
            let min = Infinity, max = -Infinity;
            for (const v of values) {
                const n = Number(v);
                if (isFinite(n)) {
                    if (n < min) min = n;
                    if (n > max) max = n;
                }
            }
            if (!isFinite(min) || max === min) return values.map(() => undefined);
            const range = max - min;
            return values.map(v => {
                const n = Number(v);
                if (!isFinite(n)) return undefined;
                return intensityToTint((n - min) / range);
            });
        });
    }, [sortedRows, heatmapMode, theme.palette.mode]);

    // Delayed skeleton: only show after a delay to prevent flash for fast loads
    const [showSkeleton, setShowSkeleton] = useState(false);
    useEffect(() => {
        if (props.isLoading && !props.data) {
            const timer = setTimeout(() => setShowSkeleton(true), SKELETON_DELAY_MS);
            return () => clearTimeout(timer);
        }
        setShowSkeleton(false);
    }, [props.isLoading, props.data]);


    // Drag-to-scroll: click and drag to scroll the center grid
    const handlePivotMouseDown = (e) => {
        // The row-detail modal portals to <body>, but its events still bubble here through the React
        // tree — so a mousedown inside the modal would otherwise start a drag-to-scroll that captures
        // the pointer and sets body { user-select: none }, blocking interaction. Ignore any event that
        // didn't physically originate inside the table's own DOM.
        if (!pivotOuterRef.current?.contains(e.target)) return;
        dragState.current = {
            isDragging: true,
            startX: e.clientX,
            startY: e.clientY,
            scrollLeft: scrollPosRef.current.scrollLeft,
            scrollTop: scrollPosRef.current.scrollTop,
            direction: null,
            wasDragging: false,
        };
        document.body.style.userSelect = 'none';
    };

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!dragState.current.isDragging) return;
            const dx = e.clientX - dragState.current.startX;
            const dy = e.clientY - dragState.current.startY;

            // Wait for enough movement to determine direction
            if (!dragState.current.direction) {
                if (Math.abs(dx) < 5 && Math.abs(dy) < 5) return;
                dragState.current.direction = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical';
            }

            e.preventDefault();

            if (centerGridRef.current) {
                if (dragState.current.direction === 'horizontal') {
                    centerGridRef.current.scrollToPosition({
                        scrollLeft: Math.max(0, dragState.current.scrollLeft - dx),
                        scrollTop: scrollPosRef.current.scrollTop,
                    });
                } else {
                    centerGridRef.current.scrollToPosition({
                        scrollLeft: scrollPosRef.current.scrollLeft,
                        scrollTop: Math.max(0, dragState.current.scrollTop - dy),
                    });
                }
            }
        };

        const handleMouseUp = () => {
            if (!dragState.current.isDragging) return;
            dragState.current.wasDragging = dragState.current.direction !== null;
            dragState.current.isDragging = false;
            dragState.current.direction = null;
            document.body.style.removeProperty('user-select');
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, []);

    // Column sort click handler — skips if a drag just occurred
    const handleColumnSort = (colIndex) => {
        if (dragState.current.wasDragging) {
            dragState.current.wasDragging = false;
            return;
        }
        setSortState(prev => {
            if (prev.colIndex !== colIndex) {
                return { colIndex, direction: 'desc' };
            }
            if (prev.direction === 'desc') return { colIndex, direction: 'asc' };
            return { colIndex: null, direction: null };
        });
    };

    // Row click handler — opens detail modal, skips if a drag just occurred
    const handleRowClick = (rowIndex) => {
        if (dragState.current.wasDragging) {
            dragState.current.wasDragging = false;
            return;
        }
        if (!isPivotRowDrillable(sortedRows[rowIndex])) return;
        setSelectedRowIndex(rowIndex);
    };

    // Hover state management via DOM class manipulation (avoids re-renders)
    const hoverState = useRef({ row: null, col: null });

    const handlePivotHover = (e) => {
        const outer = pivotOuterRef.current;
        if (!outer) return;
        // Ignore hover events that bubbled out of the portaled modal (see handlePivotMouseDown).
        if (!outer.contains(e.target)) return;

        const rowEl = e.target.closest('[data-row]');
        const colEl = e.target.closest('[data-col]');
        const rowIdx = rowEl?.dataset.row ?? null;
        const colIdx = colEl?.dataset.col ?? null;

        if (hoverState.current.row !== rowIdx) {
            outer.querySelectorAll('.pivot-row-hover').forEach(el => el.classList.remove('pivot-row-hover'));
            if (rowIdx !== null) {
                outer.querySelectorAll(`[data-row="${rowIdx}"]`).forEach(el => el.classList.add('pivot-row-hover'));
            }
            hoverState.current.row = rowIdx;
        }

        if (hoverState.current.col !== colIdx) {
            outer.querySelectorAll('.pivot-col-hover').forEach(el => el.classList.remove('pivot-col-hover'));
            if (colIdx !== null) {
                outer.querySelectorAll(`[data-col="${colIdx}"]`).forEach(el => el.classList.add('pivot-col-hover'));
            }
            hoverState.current.col = colIdx;
        }
    };

    const handlePivotHoverLeave = () => {
        const outer = pivotOuterRef.current;
        if (!outer) return;
        outer.querySelectorAll('.pivot-row-hover').forEach(el => el.classList.remove('pivot-row-hover'));
        outer.querySelectorAll('.pivot-col-hover').forEach(el => el.classList.remove('pivot-col-hover'));
        hoverState.current = { row: null, col: null };
    };

    // Sort indicator rendering
    const renderSortIndicator = (direction) => {
        return (
            <Box
                sx={{
                    flexShrink:0,
                    display:"flex",
                    alignItems:"center",
                    justifyContent:"center",
                    background:theme.palette.mode === "dark" ? colors.blue[100]  : colors.primary["100"],
                    width:24,
                    minWidth:24,
                    height:24,
                    borderRadius:"16px",
                    padding:0,
                    textTransform:"none",
                    boxShadow: "none",
                    opacity:1,
                    color:theme.palette.mode === "dark" ? colors.blue[400]  : colors.primary["400"],
                    ...(direction === 'asc' ? { transform:"rotate(180deg) scaleX(-1)" } : {}),
                }}
            >
                <SortIcon fontSize="small" />
            </Box>
        );
    };

    const columnCount = props.data?.columns?.length || 0;
    const rowCount = sortedRows.length;

    // Border styles for reuse
    const rowBorderBottom = `1px solid ${theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100]}`;
    const headerBorderBottom = `2px solid ${theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100]}`;
    const shadowColor = theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200];
    const bgColor = theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0];

    return (
        <Box
            ref={pivotOuterRef}
            onMouseDown={!props.isError ? handlePivotMouseDown : undefined}
            onMouseOver={!props.isError ? handlePivotHover : undefined}
            onMouseLeave={!props.isError ? handlePivotHoverLeave : undefined}
            sx={{
                // mt:5,
                width:1,
                minHeight:"calc(100% - 0px)",
                // minHeight:"calc(100% - 189.5px)",
                background: bgColor,
                position:"relative",
                zIndex:2,

                pt:4,
                px:4,

                "&:before" : {
                    content: "''",
                    display:"block",
                    width:"32px",
                    height:"32px",
                    position:"absolute",
                    left:0,
                    zIndex:1,
                    backgroundColor: bgColor,
                    top:"0px",
                    height:"48px",
                    width:"24px",
                    borderTopLeftRadius:"24px",
                    boxShadow: `0 -24px 0 0 ${colors.bg["tertiary"]}`,
                    transition:"250ms"
                },

                "&:after" : {
                    content: "''",
                    display:"block",
                    width:"32px",
                    height:"32px",
                    position:"absolute",
                    top:0,
                    zIndex:1,
                    backgroundColor: bgColor,
                    right:0,
                    height:"48px",
                    width:"24px",
                    borderTopLeftRadius:"24px",
                    boxShadow: `0 -24px 0 0 ${colors.bg["tertiary"]}`,
                    transform:"scaleX(-1)",
                    transition:"250ms"
                },

                "@media screen and (max-width: 1024px)" : {
                    mt:3,
                },
                "@media screen and (max-width: 47.9375em)" : {
                    "&:after, &:before" : {
                        top:"-0px"
                    }
                },
                "& .pivot-table-header" : {
                    height:72,
                    // borderBottom:`2px solid ${colors.grey[100]}`,
                    borderBottom:`2px solid ${theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100]}`,
                    display:'flex',
                    alignItems:"center",
                },

                "& .pivot-table-column, .pivot-table-column-footer":{
                    minWidth:200,
                    flex:1,
                },
                "& .pivot-table-column-footer":{
                    background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100],

                },
                "& .pivot-col-hover" : {
                    background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100],
                },
                "& .pivot-table-column-footer.pivot-col-hover" : {
                    background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200],
                },

                "& .pivot-table-row" :{
                    height:40,
                    display:'flex',
                    alignItems:"center",
                    borderBottom:`1px solid ${theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100]}`
                },
                "& .pivot-row-hover" : {
                    background:heatmapMode === "off" ? theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100] : "rgba(0,0,0,.05)",
                },
                "& .pivot-row-hover" : {
                    background:heatmapMode === "off" ? theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100] : "rgba(0,0,0,.05)",
                },
                "& .pivot-col-hover" : {
                    background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100],
                },
                "& .pivot-footer-cell" : {
                    background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100],

                },
                "& .pivot-footer-cell.pivot-col-hover" : {
                    background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200],
                },
                ...props.sx?.main
            }}
        >

            <AnimatePresence mode="wait">
                {props.isError ? (
                    <motion.div
                        key="error"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2, ease: "easeInOut" }}
                    >
                        <ShGridError/>
                    </motion.div>
                ) : props.data ? (
                    <motion.div
                        key="pivot-data"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.15, ease: "easeInOut" }}
                        style={{ height: "100%", width: "100%" }}
                    >
                        {view.mode === pivotViewModes.PERIOD_OVER_PERIOD ? (
                        <RhPivotPeriodMode data={props.data} rows={sortedRows} chartOption={props.chartOption} currentReport={props.currentReport} setCurrentReport={props.setCurrentReport} onRowClick={handleRowClick} />
                        ) : view.mode === pivotViewModes.COMPARE_PREVIOUS ? (
                        <RhPivotCompareMode rows={sortedRows} source="previous" compareLabel={t('vs_prev_period')} periods={props.data?.comparisonPeriods} chartOption={props.chartOption} currentReport={props.currentReport} setCurrentReport={props.setCurrentReport} onRowClick={handleRowClick} />
                        ) : view.mode === pivotViewModes.COMPARE ? (
                        <RhPivotCompareMode rows={sortedRows} source="lastYear" compareLabel={t('vs_last_year')} periods={props.data?.comparisonPeriods} chartOption={props.chartOption} currentReport={props.currentReport} setCurrentReport={props.setCurrentReport} onRowClick={handleRowClick} />
                        ) : (
                        <WindowScroller>
                            {({ height, scrollTop: windowScrollTop }) => (
                                <AutoSizer disableHeight>
                                    {({ width: containerWidth }) => {
                                        const rightWidth = Math.max(150, Math.floor((containerWidth - LEFT_WIDTH) / 7));
                                        const centerWidth = Math.max(0, containerWidth - LEFT_WIDTH - rightWidth);
                                        // A date pivot has more columns than fit and scrolls, so a fixed width is right.
                                        // A fixed-dimension pivot has a handful and would leave the rest of the table
                                        // empty, so those share the space instead of huddling on the left.
                                        const columnWidth = (columnCount > 0 && columnCount * COLUMN_WIDTH < centerWidth)
                                            ? Math.floor(centerWidth / columnCount)
                                            : COLUMN_WIDTH;
                                        return containerWidth > 0 ? (
                                            <ScrollSync>
                                                {({ scrollLeft, onScroll }) => {
                                                    // Keep scroll position ref in sync for drag-to-scroll
                                                    scrollPosRef.current = { scrollLeft, scrollTop: windowScrollTop };

                                                    return (
                                                        <>
                                                            {/* === HEADER ROW === */}
                                                            <Box 
                                                                sx={{ 
                                                                    display: 'flex',
                                                                    width: containerWidth, 
                                                                    height: headerHeight,
                                                                    position:'sticky',
                                                                    top:72,
                                                                    background:theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                                                                    zIndex:1,
                                                                }}
                                                            >
                                                                {/* Left header: pivot row config */}
                                                                <Box
                                                                    sx={{
                                                                        width: LEFT_WIDTH,
                                                                        height: headerHeight,
                                                                        boxShadow: `0px 0px 19px ${shadowColor}`,
                                                                        clipPath: 'inset(0px -19px 0px 0px)',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent:"space-between",
                                                                        borderBottom: headerBorderBottom,
                                                                        paddingRight: 1.5,
                                                                        gap:1,
                                                                        boxSizing: 'border-box',
                                                                    }}
                                                                >
                                                                    <Box
                                                                        sx={{
                                                                            flex:1,
                                                                            flexShrink:0,
                                                                        }}
                                                                    >
                                                                        <RhPivotTableRow
                                                                            chartOption={props.chartOption}
                                                                            currentReport={props.currentReport}
                                                                            setCurrentReport={props.setCurrentReport}
                                                                        />
                                                                    </Box>
                                                                    {view.trends.includes(pivotTrendVariants.LAST_YEAR) &&
                                                                        <Box
                                                                            sx={{
                                                                                width:130,
                                                                                flexShrink:0,
                                                                                display:'flex',
                                                                                alignItems:"center",
                                                                                justifyContent:"flex-end",
                                                                            }}
                                                                        >
                                                                            <Text>{t('vs_last_year')}</Text>
                                                                        </Box>
                                                                    }
                                                                    {view.trends.includes(pivotTrendVariants.PREVIOUS) &&
                                                                        <Box
                                                                            sx={{
                                                                                width:130,
                                                                                flexShrink:0,
                                                                                display:'flex',
                                                                                alignItems:"center",
                                                                                justifyContent:"flex-end",
                                                                            }}
                                                                        >
                                                                            <Text>{t('vs_prev_period')}</Text>
                                                                        </Box>
                                                                    }
                                                                    {view.trends.includes(pivotTrendVariants.CURRENT) &&
                                                                        <Box
                                                                            sx={{
                                                                                width:130,
                                                                                flexShrink:0,
                                                                                display:'flex',
                                                                                alignItems:"center",
                                                                                justifyContent:"flex-end",
                                                                            }}
                                                                        >
                                                                            <Text>{t('current_period')}</Text>
                                                                        </Box>
                                                                    }
                                                                </Box>

                                                                {/* Center header: column names (scroll-synced) */}
                                                                <Grid
                                                                    width={centerWidth}
                                                                    height={headerHeight}
                                                                    columnCount={columnCount}
                                                                    columnWidth={columnWidth}
                                                                    rowCount={1}
                                                                    rowHeight={headerHeight}
                                                                    scrollLeft={scrollLeft}
                                                                    cellRenderer={({ columnIndex, key, style }) => {
                                                                        const scanMeta = scanStatusColumns[columnIndex];
                                                                        const accent = scanStatusColor(scanMeta, colors, theme);

                                                                        return (
                                                                            <Box
                                                                                key={key}
                                                                                sx={{
                                                                                    ...style,
                                                                                    display: 'flex',
                                                                                    flexDirection: scanMeta ? 'column' : 'row',
                                                                                    alignItems: scanMeta ? 'flex-start' : 'center',
                                                                                    justifyContent: scanMeta ? 'center' : 'space-between',
                                                                                    gap: scanMeta ? 0.5 : 0,
                                                                                    borderBottom: headerBorderBottom,
                                                                                    padding: scanMeta ? '12px 16px' : '0 12px',
                                                                                    cursor: 'pointer',
                                                                                    userSelect: 'none',
                                                                                    boxSizing: 'border-box',
                                                                                    // Without the tint a header carrying a line of explanation reads as
                                                                                    // another data row; this is what keeps the band legible as chrome.
                                                                                    background: scanMeta ? alpha(accent, theme.palette.mode === 'dark' ? 0.16 : 0.09) : undefined,
                                                                                }}
                                                                                data-col={columnIndex}
                                                                                onClick={() => handleColumnSort(columnIndex)}
                                                                            >
                                                                                {scanMeta ? (
                                                                                    <>
                                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: 1 }}>
                                                                                            {/* <Box sx={{ width: 9, height: 9, borderRadius: '3px', background: accent, flex: 'none' }} /> */}
                                                                                            <Text bold sx={{ color: accent, userSelect: 'none' }} noBreak>
                                                                                                {t(scanMeta.key)}
                                                                                            </Text>
                                                                                            <EMScanStatus meta={scanMeta} shippers={props.data?.shippers} />
                                                                                            <Box
                                                                                                sx={{
                                                                                                    ml:"auto",
                                                                                                }}
                                                                                            >
                                                                                            {sortState.colIndex === columnIndex && renderSortIndicator(sortState.direction)}
                                                                                            </Box>
                                                                                        </Box>
                                                                                        {/* Every bucket gets the same single line, so the four cells read as
                                                                                            one band instead of four differently-filled boxes. The rest of
                                                                                            the explanation, and who falls under it, sits in the explainer. */}
                                                                                        <Text light size="12px" sx={{ userSelect: 'none', lineHeight: 1.4 }}>
                                                                                            {t(scanMeta.ruleKey)}
                                                                                        </Text>
                                                                                    </>
                                                                                ) : (
                                                                                    <>
                                                                                        <Text
                                                                                            bold={sortState.colIndex === columnIndex}
                                                                                            sx={{ userSelect: "none" }}
                                                                                            noBreak
                                                                                        >
                                                                                            {props.data.columns[columnIndex]}
                                                                                        </Text>
                                                                                        {sortState.colIndex === columnIndex && renderSortIndicator(sortState.direction)}
                                                                                    </>
                                                                                )}
                                                                            </Box>
                                                                        );
                                                                    }}
                                                                    style={{ overflow: 'hidden', outline: 'none' }}
                                                                />

                                                                {/* Right header: "Total" label */}
                                                                <Box
                                                                    sx={{
                                                                        width: rightWidth,
                                                                        height: headerHeight,
                                                                        boxShadow: `0px 0px 19px ${shadowColor}`,
                                                                        clipPath: 'inset(0px 0px 0px -19px)',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'space-between',
                                                                        borderBottom: headerBorderBottom,
                                                                        padding: '0 24px 0 24px',
                                                                        cursor: 'pointer',
                                                                        userSelect: 'none',
                                                                    }}
                                                                    onClick={() => handleColumnSort('total')}
                                                                >
                                                                    <Subheading textAlign="right">
                                                                        {t("total")}
                                                                    </Subheading>
                                                                    {sortState.colIndex === 'total' && renderSortIndicator(sortState.direction)}
                                                                </Box>
                                                            </Box>

                                                            {/* === BODY ROWS (virtualized) === */}
                                                            <Box sx={{ display: 'flex', width: containerWidth, cursor: 'grab' }}>
                                                                {/* Left body: row labels */}
                                                                <Grid
                                                                    autoHeight
                                                                    width={LEFT_WIDTH}
                                                                    height={height}
                                                                    columnCount={1}
                                                                    columnWidth={LEFT_WIDTH}
                                                                    rowCount={rowCount}
                                                                    rowHeight={ROW_HEIGHT}
                                                                    scrollTop={windowScrollTop}
                                                                    cellRenderer={({ rowIndex, key, style }) => (
                                                                        <Box
                                                                            key={key}
                                                                            sx={{
                                                                                ...style,
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'space-between',
                                                                                gap: 1,
                                                                                borderBottom: rowBorderBottom,
                                                                                boxSizing: 'border-box',
                                                                                paddingRight: '12px',
                                                                                cursor: pivotRowCursor(sortedRows[rowIndex]),
                                                                            }}
                                                                            data-row={rowIndex}
                                                                            onClick={() => handleRowClick(rowIndex)}
                                                                        >
                                                                            <Text 
                                                                                sx={{
                                                                                    overflow:"hidden",
                                                                                    whiteSpace:"nowrap",
                                                                                    textOverflow:"ellipsis",
                                                                                    flex:1,
                                                                                    flexShrink:0,

                                                                                }}
                                                                            >
                                                                                {t(sortedRows[rowIndex].name) || t(sortedRows[rowIndex].description)}
                                                                            </Text>
                                                                            {view.trends.length > 0 &&
                                                                                <>
                                                                                    <RhTrendPills row={sortedRows[rowIndex]} trends={view.trends} />

                                                                                    {view.trends.includes(pivotTrendVariants.CURRENT) &&
                                                                                        <Box
                                                                                            sx={{
                                                                                                width:130,
                                                                                                flexShrink:0,
                                                                                                display:'flex',
                                                                                                alignItems:"center",
                                                                                                justifyContent:"flex-end",
                                                                                            }}
                                                                                        >
                                                                                            <RhDeltaPill values={sortedRows[rowIndex].values} />
                                                                                        </Box>
                                                                                    }
                                                                                </>
                                                                            }
                                                                        </Box>
                                                                    )}
                                                                    style={{
                                                                        overflow: 'hidden',
                                                                        outline: 'none',
                                                                        boxShadow: `0px 0px 19px ${shadowColor}`,
                                                                        clipPath: 'inset(0px -19px 0px 0px)',
                                                                    }}
                                                                />

                                                                {/* Center body: data cells (main scroller) */}
                                                                <Grid
                                                                    autoHeight
                                                                    ref={centerGridRef}
                                                                    width={centerWidth}
                                                                    height={height}
                                                                    columnCount={columnCount}
                                                                    columnWidth={columnWidth}
                                                                    rowCount={rowCount}
                                                                    rowHeight={ROW_HEIGHT}
                                                                    scrollTop={windowScrollTop}
                                                                    onScroll={onScroll}
                                                                    overscanColumnCount={3}
                                                                    overscanRowCount={5}
                                                                    cellRenderer={({ columnIndex, rowIndex, key, style }) => {
                                                                        const scanMeta = scanStatusColumns[columnIndex];
                                                                        const accent = scanStatusColor(scanMeta, colors, theme);
                                                                        // The heatmap is an explicit choice by the user, so it wins; the
                                                                        // bucket tint only carries the column when no heatmap is on.
                                                                        const heatmapBg = heatmapColors?.[rowIndex]?.[columnIndex];
                                                                        const scanBg = (!heatmapBg && scanMeta?.tint)
                                                                            ? alpha(accent, theme.palette.mode === 'dark' ? 0.08 : 0.05)
                                                                            : undefined;

                                                                        return (
                                                                            <Box
                                                                                key={key}
                                                                                sx={{
                                                                                    ...style,
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    borderBottom: rowBorderBottom,
                                                                                    padding: '0 12px',
                                                                                    boxSizing: 'border-box',
                                                                                    userSelect:"none",
                                                                                    cursor: pivotRowCursor(sortedRows[rowIndex]),
                                                                                    background: heatmapBg ?? scanBg,
                                                                                    "&:hover" : {
                                                                                        background:`${theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200]} !important`,
                                                                                    }
                                                                                }}
                                                                                data-row={rowIndex}
                                                                                data-col={columnIndex}
                                                                                onClick={() => handleRowClick(rowIndex)}
                                                                            >
                                                                                {/* The tinted column already carries the signal, so the number
                                                                                    itself stays plain text and only gains weight. */}
                                                                                <Text bold={scanMeta?.alarming}>
                                                                                    {sortedRows[rowIndex].values[columnIndex]}
                                                                                </Text>
                                                                            </Box>
                                                                        );
                                                                    }}
                                                                    style={{ outline: 'none' }}
                                                                />

                                                                {/* Right body: row totals */}
                                                                <Grid
                                                                    autoHeight
                                                                    width={rightWidth}
                                                                    height={height}
                                                                    columnCount={1}
                                                                    columnWidth={rightWidth}
                                                                    rowCount={rowCount}
                                                                    rowHeight={ROW_HEIGHT}
                                                                    scrollTop={windowScrollTop}
                                                                    cellRenderer={({ rowIndex, key, style }) => (
                                                                        <Box
                                                                            key={key}
                                                                            sx={{
                                                                                ...style,
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                borderBottom: rowBorderBottom,
                                                                                padding: '8px 8px 8px 24px',
                                                                                boxSizing: 'border-box',
                                                                                cursor: pivotRowCursor(sortedRows[rowIndex]),
                                                                            }}
                                                                            data-row={rowIndex}
                                                                            onClick={() => handleRowClick(rowIndex)}
                                                                        >
                                                                            <Text bold>
                                                                                {sortedRows[rowIndex].total}
                                                                            </Text>
                                                                        </Box>
                                                                    )}
                                                                    style={{
                                                                        overflow: 'hidden',
                                                                        outline: 'none',
                                                                        boxShadow: `0px 0px 19px ${shadowColor}`,
                                                                        clipPath: 'inset(0px 0px 0px -19px)',
                                                                    }}
                                                                />
                                                            </Box>

                                                            {/* === FOOTER ROW === */}
                                                            <Box sx={{ 
                                                                position:"sticky",
                                                                bottom:0,
                                                                display: 'flex', 
                                                                width: containerWidth, 
                                                                height: FOOTER_HEIGHT, 
                                                                background:theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                                                                zIndex:1,
                                                            }}>
                                                                {/* Left footer: empty */}
                                                                <Box
                                                                    style={{
                                                                        width: LEFT_WIDTH,
                                                                        height: FOOTER_HEIGHT,
                                                                        boxShadow: `0px 0px 19px ${shadowColor}`,
                                                                        clipPath: 'inset(0px -19px 0px 0px)',
                                                                    }}
                                                                />

                                                                {/* Center footer: column totals (scroll-synced) */}
                                                                <Box
                                                                    sx={{
                                                                        height:FOOTER_HEIGHT,
                                                                        background:`${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[100]}`
                                                                    }}
                                                                >
                                                                    <Grid
                                                                        width={centerWidth}
                                                                        height={ROW_HEIGHT}
                                                                        columnCount={columnCount}
                                                                        columnWidth={columnWidth}
                                                                        rowCount={1}
                                                                        rowHeight={ROW_HEIGHT}
                                                                        scrollLeft={scrollLeft}
                                                                        cellRenderer={({ columnIndex, key, style }) => (
                                                                            <Box
                                                                                key={key}
                                                                                className="pivot-footer-cell"
                                                                                style={{
                                                                                    ...style,
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    padding: '0 12px',
                                                                                    userSelect: 'none',
                                                                                    boxSizing: 'border-box',
                                                                                }}
                                                                                data-col={columnIndex}
                                                                            >
                                                                                <Text sx={{ userSelect: "none" }}>
                                                                                    {props.data.columnTotals[columnIndex]}
                                                                                </Text>
                                                                            </Box>
                                                                        )}
                                                                        style={{ overflow: 'hidden', outline: 'none' }}
                                                                    />
                                                                </Box>

                                                                {/* Right footer: grand total */}
                                                                <Box
                                                                    sx={{
                                                                        height:FOOTER_HEIGHT,
                                                                        background: theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100],
                                                                    }}
                                                                >
                                                                    <Box
                                                                        sx={{
                                                                            width: rightWidth,
                                                                            height: ROW_HEIGHT,
                                                                            boxShadow: `0px 0px 19px ${shadowColor}`,
                                                                            clipPath: 'inset(0px 0px 0px -19px)',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            padding: '0 24px',
                                                                            boxSizing: 'border-box',
                                                                        }}
                                                                    >
                                                                        <Subheading variant={theme.palette.mode === "dark" ? "blue" : "primary"}>
                                                                            {props.data?.grandTotal}
                                                                        </Subheading>
                                                                    </Box>
                                                                </Box>
                                                            </Box>
                                                        </>
                                                    );
                                                }}
                                            </ScrollSync>
                                        ) : <div />;
                                    }}
                                </AutoSizer>
                            )}
                        </WindowScroller>
                        )}
                        {props.actions && props.actions}
                    </motion.div>
                ) : showSkeleton ? (
                    <motion.div
                        key="skeleton"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.15, ease: "easeInOut" }}
                        style={{ height: "100%", width: "100%" }}
                    >
                        {/* Skeleton header */}
                        <Box
                            sx={{
                                width:1,
                                display:"flex",
                            }}
                        >
                            <Box sx={{ width:300 }}>
                                <Box
                                    sx={{
                                        height: headerHeight,
                                        borderBottom: headerBorderBottom,
                                        display: 'flex',
                                        alignItems: 'center',
                                        pr:3,
                                    }}
                                >
                                    <ShTextSkeleton sx={{ width: 120 }}/>
                                </Box>
                            </Box>
                            <Box sx={{ flex:6, display:"flex" }}>
                                {[...Array(5)].map((_, i) => (
                                    <Box key={i} sx={{ minWidth: COLUMN_WIDTH, flex: 1 }}>
                                        <Box
                                            sx={{
                                                height: headerHeight,
                                                borderBottom: headerBorderBottom,
                                                display: 'flex',
                                                alignItems: 'center',
                                                px:1.5,
                                            }}
                                        >
                                            <ShTextSkeleton sx={{ width: 80 }}/>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                            <Box sx={{ flex:1 }}>
                                <Box
                                    sx={{
                                        height: headerHeight,
                                        borderBottom: headerBorderBottom,
                                        display: 'flex',
                                        alignItems: 'center',
                                        px:3,
                                    }}
                                >
                                    <ShTextSkeleton sx={{ width: 60 }}/>
                                </Box>
                            </Box>
                        </Box>
                        {/* Skeleton rows */}
                        <Box sx={{ display:"flex" }}>
                            <Box sx={{ width:300 }}>
                                {SKELETON_WIDTHS.map((w, i) => (
                                    <Box key={i} sx={{ height: ROW_HEIGHT, display: 'flex', alignItems: 'center', borderBottom: rowBorderBottom, pr:2 }}>
                                        <ShTextSkeleton sx={{ width: w.left }}/>
                                    </Box>
                                ))}
                            </Box>
                            <Box sx={{ flex:6, display:"flex" }}>
                                {[...Array(5)].map((_, col) => (
                                    <Box key={col} sx={{ minWidth: COLUMN_WIDTH, flex: 1 }}>
                                        {SKELETON_WIDTHS.map((w, i) => (
                                            <Box key={i} sx={{ height: ROW_HEIGHT, display: 'flex', alignItems: 'center', borderBottom: rowBorderBottom, px:1.5 }}>
                                                <ShTextSkeleton sx={{ width: w.mid }}/>
                                            </Box>
                                        ))}
                                    </Box>
                                ))}
                            </Box>
                            <Box sx={{ flex:1 }}>
                                {SKELETON_WIDTHS.map((w, i) => (
                                    <Box key={i} sx={{ height: ROW_HEIGHT, display: 'flex', alignItems: 'center', borderBottom: rowBorderBottom, pl:3, p:1 }}>
                                        <ShTextSkeleton sx={{ width: w.right }}/>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </motion.div>
                ) : null}
            </AnimatePresence>

            <SHModal
                open={selectedRowIndex !== null}
                onClose={() => setSelectedRowIndex(null)}
                sx={{
                    body:{
                        width: "96%",
                        maxWidth: 2200,
                        maxHeight: "92vh",
                        display: "flex",
                        flexDirection: "column",
                    }
                }}
            >
                {/* The close button hangs outside the body, so the body itself must not clip; this inner box scrolls
                    instead. Its scrollbar is kept thin and its track starts below the rounded corners so the
                    corners stay intact. */}
                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        overflow: "auto",
                        borderRadius: 6,
                        scrollbarWidth: "thin",
                        scrollbarColor: `${alpha(colors.txt["secondary"], 0.35)} transparent`,
                        "&::-webkit-scrollbar": { width: 8 },
                        "&::-webkit-scrollbar-track": { background: "transparent", margin: "24px 0" },
                        "&::-webkit-scrollbar-thumb": { background: alpha(colors.txt["secondary"], 0.35), borderRadius: 4 },
                    }}
                >
                    {selectedRowIndex !== null && sortedRows[selectedRowIndex] && (
                        <RhPivotRowDetail
                            currentReport={props.currentReport}
                            row={sortedRows[selectedRowIndex]}
                            chartOption={props.chartOption}
                            allRows={props.data?.rows ?? []}
                        />
                    )}
                </Box>
            </SHModal>
        </Box>
    )
}
